import React from "react";
import clsx, { type ClassValue, } from "clsx";
import { twMerge, } from "tailwind-merge";

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Reproduz o "gutter" do MUI Grid: container ganha margin negativa + width estendido,
// e padding é aplicado em cada filho direto. Assim 2 itens xs={6} (50%+50%) cabem no container.
// MUI v4 default theme: theme.spacing(n) = n * 8px.
const spacingContainerMap = {
  0: "",
  1: "-m-1 w-[calc(100%+8px)] [&>*]:p-1",
  2: "-m-2 w-[calc(100%+16px)] [&>*]:p-2",
  3: "-m-3 w-[calc(100%+24px)] [&>*]:p-3",
  4: "-m-4 w-[calc(100%+32px)] [&>*]:p-4",
  5: "-m-5 w-[calc(100%+40px)] [&>*]:p-5",
  6: "-m-6 w-[calc(100%+48px)] [&>*]:p-6",
  7: "-m-7 w-[calc(100%+56px)] [&>*]:p-7",
  8: "-m-8 w-[calc(100%+64px)] [&>*]:p-8",
  9: "-m-9 w-[calc(100%+72px)] [&>*]:p-9",
  10: "-m-10 w-[calc(100%+80px)] [&>*]:p-10",
} as const;

export type GridSize = false | "auto" | true | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

export interface GridProps extends React.HTMLAttributes<HTMLDivElement> {
  container?: boolean;
  item?: boolean;
  spacing?: keyof typeof spacingContainerMap;
  direction?: "row" | "row-reverse" | "column" | "column-reverse";
  wrap?: "nowrap" | "wrap" | "wrap-reverse";
  alignItems?: "flex-start" | "center" | "flex-end" | "stretch" | "baseline";
  alignContent?: "stretch" | "center" | "flex-start" | "flex-end" | "space-between" | "space-around";
  justifyContent?: "flex-start" | "center" | "flex-end" | "space-between" | "space-around" | "space-evenly";
  /** @deprecated Use `justifyContent` instead, the prop was renamed. */
  justify?: "flex-start" | "center" | "flex-end" | "space-between" | "space-around" | "space-evenly";
  xs?: GridSize;
  sm?: GridSize;
  md?: GridSize;
  lg?: GridSize;
  xl?: GridSize;
  component?: React.ElementType;
  zeroMinWidth?: boolean;
}

const directionMap = {
  "row": "flex-row",
  "row-reverse": "flex-row-reverse",
  "column": "flex-col",
  "column-reverse": "flex-col-reverse",
} as const;

const wrapMap = {
  "nowrap": "flex-nowrap",
  "wrap": "flex-wrap",
  "wrap-reverse": "flex-wrap-reverse",
} as const;

const alignItemsMap = {
  "flex-start": "items-start",
  "center": "items-center",
  "flex-end": "items-end",
  "stretch": "items-stretch",
  "baseline": "items-baseline",
} as const;

const alignContentMap = {
  "stretch": "content-stretch",
  "center": "content-center",
  "flex-start": "content-start",
  "flex-end": "content-end",
  "space-between": "content-between",
  "space-around": "content-around",
} as const;

const justifyContentMap = {
  "flex-start": "justify-start",
  "center": "justify-center",
  "flex-end": "justify-end",
  "space-between": "justify-between",
  "space-around": "justify-around",
  "space-evenly": "justify-evenly",
} as const;

// Mapas estáticos por breakpoint para o JIT do Tailwind detectar todas as classes.
// Reproduz o MUI Grid: numeric -> flexBasis = N/12%, flexGrow = 0, maxWidth = N/12%.
//                     true    -> flexBasis 0, flexGrow 1, maxWidth 100%.
//                     auto    -> flexBasis auto, flexGrow 0, maxWidth none, width auto.
//
// Breakpoints MUI v4 (não os padrões do Tailwind):
//   xs: 0px | sm: 600px | md: 960px | lg: 1280px | xl: 1920px
const xsGridMap = {
  "true": "grow basis-0 max-w-full",
  "auto": "grow-0 basis-auto w-auto max-w-none",
  "1": "grow-0 basis-1/12 max-w-[8.333333%]",
  "2": "grow-0 basis-2/12 max-w-[16.666667%]",
  "3": "grow-0 basis-3/12 max-w-[25%]",
  "4": "grow-0 basis-4/12 max-w-[33.333333%]",
  "5": "grow-0 basis-5/12 max-w-[41.666667%]",
  "6": "grow-0 basis-6/12 max-w-[50%]",
  "7": "grow-0 basis-7/12 max-w-[58.333333%]",
  "8": "grow-0 basis-8/12 max-w-[66.666667%]",
  "9": "grow-0 basis-9/12 max-w-[75%]",
  "10": "grow-0 basis-10/12 max-w-[83.333333%]",
  "11": "grow-0 basis-11/12 max-w-[91.666667%]",
  "12": "grow-0 basis-full max-w-full",
} as const;

const smGridMap = {
  "true": "sm:grow sm:basis-0 sm:max-w-full",
  "auto": "sm:grow-0 sm:basis-auto sm:w-auto sm:max-w-none",
  "1": "sm:grow-0 sm:basis-1/12 sm:max-w-[8.333333%]",
  "2": "sm:grow-0 sm:basis-2/12 sm:max-w-[16.666667%]",
  "3": "sm:grow-0 sm:basis-3/12 sm:max-w-[25%]",
  "4": "sm:grow-0 sm:basis-4/12 sm:max-w-[33.333333%]",
  "5": "sm:grow-0 sm:basis-5/12 sm:max-w-[41.666667%]",
  "6": "sm:grow-0 sm:basis-6/12 sm:max-w-[50%]",
  "7": "sm:grow-0 sm:basis-7/12 sm:max-w-[58.333333%]",
  "8": "sm:grow-0 sm:basis-8/12 sm:max-w-[66.666667%]",
  "9": "sm:grow-0 sm:basis-9/12 sm:max-w-[75%]",
  "10": "sm:grow-0 sm:basis-10/12 sm:max-w-[83.333333%]",
  "11": "sm:grow-0 sm:basis-11/12 sm:max-w-[91.666667%]",
  "12": "sm:grow-0 sm:basis-full sm:max-w-full",
} as const;

const mdGridMap = {
  "true": "md:grow md:basis-0 md:max-w-full",
  "auto": "md:grow-0 md:basis-auto md:w-auto md:max-w-none",
  "1": "md:grow-0 md:basis-1/12 md:max-w-[8.333333%]",
  "2": "md:grow-0 md:basis-2/12 md:max-w-[16.666667%]",
  "3": "md:grow-0 md:basis-3/12 md:max-w-[25%]",
  "4": "md:grow-0 md:basis-4/12 md:max-w-[33.333333%]",
  "5": "md:grow-0 md:basis-5/12 md:max-w-[41.666667%]",
  "6": "md:grow-0 md:basis-6/12 md:max-w-[50%]",
  "7": "md:grow-0 md:basis-7/12 md:max-w-[58.333333%]",
  "8": "md:grow-0 md:basis-8/12 md:max-w-[66.666667%]",
  "9": "md:grow-0 md:basis-9/12 md:max-w-[75%]",
  "10": "md:grow-0 md:basis-10/12 md:max-w-[83.333333%]",
  "11": "md:grow-0 md:basis-11/12 md:max-w-[91.666667%]",
  "12": "md:grow-0 md:basis-full md:max-w-full",
} as const;

const lgGridMap = {
  "true": "lg:grow lg:basis-0 lg:max-w-full",
  "auto": "lg:grow-0 lg:basis-auto lg:w-auto lg:max-w-none",
  "1": "lg:grow-0 lg:basis-1/12 lg:max-w-[8.333333%]",
  "2": "lg:grow-0 lg:basis-2/12 lg:max-w-[16.666667%]",
  "3": "lg:grow-0 lg:basis-3/12 lg:max-w-[25%]",
  "4": "lg:grow-0 lg:basis-4/12 lg:max-w-[33.333333%]",
  "5": "lg:grow-0 lg:basis-5/12 lg:max-w-[41.666667%]",
  "6": "lg:grow-0 lg:basis-6/12 lg:max-w-[50%]",
  "7": "lg:grow-0 lg:basis-7/12 lg:max-w-[58.333333%]",
  "8": "lg:grow-0 lg:basis-8/12 lg:max-w-[66.666667%]",
  "9": "lg:grow-0 lg:basis-9/12 lg:max-w-[75%]",
  "10": "lg:grow-0 lg:basis-10/12 lg:max-w-[83.333333%]",
  "11": "lg:grow-0 lg:basis-11/12 lg:max-w-[91.666667%]",
  "12": "lg:grow-0 lg:basis-full lg:max-w-full",
} as const;

const xlGridMap = {
  "true": "xl:grow xl:basis-0 xl:max-w-full",
  "auto": "xl:grow-0 xl:basis-auto xl:w-auto xl:max-w-none",
  "1": "xl:grow-0 xl:basis-1/12 xl:max-w-[8.333333%]",
  "2": "xl:grow-0 xl:basis-2/12 xl:max-w-[16.666667%]",
  "3": "xl:grow-0 xl:basis-3/12 xl:max-w-[25%]",
  "4": "xl:grow-0 xl:basis-4/12 xl:max-w-[33.333333%]",
  "5": "xl:grow-0 xl:basis-5/12 xl:max-w-[41.666667%]",
  "6": "xl:grow-0 xl:basis-6/12 xl:max-w-[50%]",
  "7": "xl:grow-0 xl:basis-7/12 xl:max-w-[58.333333%]",
  "8": "xl:grow-0 xl:basis-8/12 xl:max-w-[66.666667%]",
  "9": "xl:grow-0 xl:basis-9/12 xl:max-w-[75%]",
  "10": "xl:grow-0 xl:basis-10/12 xl:max-w-[83.333333%]",
  "11": "xl:grow-0 xl:basis-11/12 xl:max-w-[91.666667%]",
  "12": "xl:grow-0 xl:basis-full xl:max-w-full",
} as const;

function getGridClasses(
  size: GridSize | undefined,
  map: Record<string, string>
): string {
  if (size === undefined || size === false) return "";

  return map[String(size)] ?? "";
}

const Grid = React.forwardRef<HTMLDivElement, GridProps>((props, ref) => {
  const {
    container = false,
    item = false,
    spacing = 0,
    direction = "row",
    wrap = "wrap",
    alignItems = "stretch",
    alignContent = "stretch",
    justifyContent = "flex-start",
    justify,
    xs = false,
    sm = false,
    md = false,
    lg = false,
    xl = false,
    component: Component = "div",
    className,
    zeroMinWidth = false,
    ...other
  } = props;

  const effectiveJustify = justify ?? justifyContent;

  const classes = cn(
    container && "box-border flex w-full",
    item && "box-border m-0",
    zeroMinWidth && "min-w-0",

    container && spacing !== 0 && spacingContainerMap[spacing],

    container && [
      directionMap[direction],
      wrapMap[wrap],
      alignItemsMap[alignItems],
      alignContentMap[alignContent],
      justifyContentMap[effectiveJustify],
    ],

    item && [
      getGridClasses(xs, xsGridMap),
      getGridClasses(sm, smGridMap),
      getGridClasses(md, mdGridMap),
      getGridClasses(lg, lgGridMap),
      getGridClasses(xl, xlGridMap),
    ],

    className
  );

  return <Component ref={ref} className={classes} {...other} />;
});

Grid.displayName = "Grid";

export default Grid;
